import { useEffect, useState } from "react";
import { Bell, BellOff, Loader2, CheckCircle2, Send } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { enablePush, pushSupported } from "../lib/push";

/* One-tap toggle that registers the browser's FCM device token via
   `POST /push/register`. Handles the four states the browser can be in:
   unsupported / default / granted / denied. Idempotent — if a token is
   already saved, tapping again refreshes it (in case Firebase rotated it).
   Persists a local hint (`tp_push_on`) so the toggle shows "On" instantly
   on subsequent visits without a round-trip. */

const LS_KEY = "tp_push_on";

export default function PushToggle() {
  const supported = pushSupported();
  const permission = supported ? Notification.permission : "unsupported";
  const [enabled, setEnabled] = useState(() => {
    try { return localStorage.getItem(LS_KEY) === "1" && permission === "granted"; }
    catch { return false; }
  });
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  // If the browser flipped permission from another tab, sync the toggle.
  useEffect(() => {
    if (permission !== "granted" && enabled) {
      setEnabled(false);
      try { localStorage.removeItem(LS_KEY); } catch { /* silent */ }
    }
  }, [permission, enabled]);

  const turnOn = async () => {
    setBusy(true);
    try {
      await enablePush();
      setEnabled(true);
      try { localStorage.setItem(LS_KEY, "1"); } catch { /* silent */ }
      toast.success("Push notifications turned on");
    } catch (e) {
      toast.error(e?.message || "Could not enable push notifications");
    } finally { setBusy(false); }
  };

  const turnOff = () => {
    setEnabled(false);
    try { localStorage.removeItem(LS_KEY); } catch { /* silent */ }
    toast.message(
      "To fully stop pushes on this device, revoke Notifications in your browser's site settings.",
      { duration: 6000 });
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      await api.post("/push/test");
      toast.success("Test push sent — check your device");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send test push");
    } finally { setTesting(false); }
  };

  if (!supported) {
    return (
      <div className="rounded-xl border border-border bg-secondary/40 p-4 flex items-start gap-3" data-testid="push-toggle-unsupported">
        <BellOff className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" strokeWidth={1.75} />
        <div className="text-sm">
          <div className="font-700 text-primary">Push notifications aren't supported here</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            Open My Social Pulse in Chrome, Firefox, Edge, or Safari on iOS 16.4+ to receive vault alerts and settle-up nudges on your device.
          </div>
        </div>
      </div>
    );
  }

  if (permission === "denied") {
    return (
      <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 flex items-start gap-3" data-testid="push-toggle-denied">
        <BellOff className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" strokeWidth={1.75} />
        <div className="text-sm">
          <div className="font-700 text-amber-900">Notifications are blocked</div>
          <div className="text-xs text-amber-800 mt-0.5">
            Your browser is blocking My Social Pulse from sending alerts. Open the site settings (padlock icon → Notifications) and allow, then reload.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-xl border p-4 flex items-start gap-3 ${enabled ? "border-emerald-300 bg-emerald-50" : "border-border bg-card"}`}
         data-testid="push-toggle">
      <Bell className={`w-5 h-5 shrink-0 mt-0.5 ${enabled ? "text-emerald-700" : "text-primary"}`} strokeWidth={1.75} />
      <div className="flex-1 min-w-0">
        <div className="font-700 text-primary flex items-center gap-1.5">
          Push notifications
          {enabled && <CheckCircle2 className="w-4 h-4 text-emerald-600" strokeWidth={2} />}
        </div>
        <div className="text-xs text-muted-foreground mt-0.5 leading-snug">
          Get lock-screen alerts for vault emergency requests, group settle-up nudges, Guardian pings and tracked flight changes. Coordinates and vault contents are never included.
        </div>
        {enabled && (
          <button onClick={sendTest} disabled={testing} data-testid="push-toggle-test"
            className="mt-2 inline-flex items-center gap-1 text-[11px] font-700 text-emerald-700 hover:underline disabled:opacity-60">
            {testing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Send me a test push
          </button>
        )}
      </div>
      {enabled ? (
        <button onClick={turnOff} data-testid="push-toggle-off"
          className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-emerald-500 bg-white text-emerald-700 text-xs font-700 hover:bg-emerald-100 min-h-[36px]">
          On
        </button>
      ) : (
        <button onClick={turnOn} disabled={busy} data-testid="push-toggle-on"
          className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-xs font-700 disabled:opacity-60 min-h-[36px]">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bell className="w-3.5 h-3.5" />} Turn on
        </button>
      )}
    </div>
  );
}
